import { t } from '../i18n.js';
import { logClientEvent, describeError } from './jsErrorLogger.js';
import { isValidPhoneNumber, getCountryCallingCode } from 'libphonenumber-js/max';

// Ochiul de parola (FB-031): SVG inline - proiectul n-are nicio icoana de ochi,
// iar tiparul vechi din register-modern folosea emoji, nedorit aici.
const EYE = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 19 1 12z"/><circle cx="12" cy="12" r="3"/></svg>`;
const EYE_OFF = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;

/**
 * Pune butonul "parola vizibila" pe FIECARE camp de parola din pas (FB-031).
 * Selectie pe tip, nu pe id - acopera login/register/reset+confirm dintr-un apel
 * si nu depinde de id-uri. Se apeleaza per-render (pasii isi refac DOM-ul).
 * type="button" obligatoriu, altfel ar submite formularul.
 */
export function bindPasswordToggles($el) {
    $el.find('input[type="password"]').each(function () {
        const $input = $(this);
        if ($input.parent().hasClass('pw-wrap')) return; // deja legat
        $input.wrap('<span class="pw-wrap"></span>');
        const $btn = $(`<button type="button" class="pw-toggle" aria-label="${t('show_password')}" aria-pressed="false">${EYE}</button>`);
        $input.after($btn);
        $btn.on('click', () => {
            const show = $input.attr('type') === 'password';
            $input.attr('type', show ? 'text' : 'password');
            $btn.attr('aria-label', t(show ? 'hide_password' : 'show_password'))
                .attr('aria-pressed', String(show))
                .html(show ? EYE_OFF : EYE);
            $input.trigger('focus');
        });
    });
}

export function bindInputListeners($form) {
    $form.find('input, select, textarea').on('input change', function () {
        const $row = $(this).closest('.form-row');
        $row.find('.field-error').hide();
        // Slotul generic stă în afara rândurilor; altfel „nu am putut trimite” de la o
        // încercare veche rămânea pe ecran lângă „parolă incorectă” de la cea nouă.
        $form.find('.field-error.field-generic-error').hide();
    });
}

/**
 * @returns {number} câte mesaje au fost chiar afișate (o cheie fără input pe pasul curent nu
 *   afișează nimic — apelantul decide ce face cu 0).
 */
export function showValidationErrors($form, errors = {}) {
    // O cerere nouă = un verdict nou: mesajul generic al încercării anterioare dispare.
    $form.find('.field-error.field-generic-error').hide();

    let shown = 0;
    for (const [fieldName, messages] of Object.entries(errors)) {
        const message = Array.isArray(messages) ? messages[0] : messages;

        // '_generic' = a message with no field to hang on (network drop on the
        // SMS step, where the inputs are digits[]). Rendered once, at the end
        // of the form, so no auth step can fail without SOME visible text.
        if (fieldName === '_generic') {
            let $generic = $form.find('.field-error.field-generic-error');
            if ($generic.length === 0) {
                $generic = $('<p class="field-error field-generic-error" style="display:none;"></p>');
                ($form.find('form').first().length ? $form.find('form').first() : $form).append($generic);
            }
            $generic.text(message).show();
            shown++;
            continue;
        }

        const $input = $form.find(`[name="${fieldName}"]`);
        if ($input.length === 0) continue;

        const $row = $input.closest('.form-row');

        let $error = $row.find(`.field-error.field-${fieldName}-error`);
        if ($error.length === 0) {
            $error = $(`<p class="field-error field-${fieldName}-error" style="display:none;"></p>`);
            $row.append($error);
        }

        $error.text(message).show();
        shown++;
    }
    return shown;
}

/**
 * true când răspunsul poartă text de eroare de la server: obiect de câmpuri (422/400, sau 404-ul
 * de la login cu „parolă greșită”) SAU listă (`{errors: ['Account not found']}` la resetare).
 */
export function hasFieldErrors(err) {
    const fields = err?.data?.errors;
    return !!fields && typeof fields === 'object' && Object.keys(fields).length > 0;
}

/**
 * Handler standard pentru pașii de auth. Regula: omul vede MEREU ceva, serverul află DOAR ce
 * n-a văzut omul ca mesaj de câmp.
 *  - erori de câmp din JSON → pe câmpurile lor; dacă nicio cheie n-are input pe pasul curent
 *    (422 pe `phone` în pasul de cod) sau vin ca listă, primul mesaj intră în slotul generic.
 *    Nu se loghează: parola greșită / email folosit erau 17 din 22 de linii pe 9 sept 2026.
 *  - ORICE altceva (5xx, HTML de la load balancer, fetch căzut) → mesaj generic + log. Înainte, un
 *    status fără JSON afișa {} = nimic: 502-ul din 9 sept 2026 a fost mut.
 *  - 419 → „sesiunea a expirat, reîncarcă”: „verifică conexiunea” ar trimite omul într-o buclă.
 */
export function reportRequestError($el, err, step) {
    console.warn('Failed:', err);
    const status = err?.status ?? null;
    const fields = err?.data?.errors;

    if (hasFieldErrors(err)) {
        const shown = Array.isArray(fields) ? 0 : showValidationErrors($el, fields);
        if (!shown) {
            const first = Array.isArray(fields) ? fields[0] : Object.values(fields)[0];
            const text = Array.isArray(first) ? first[0] : first;
            showValidationErrors($el, {_generic: typeof text === 'string' ? text : t('auth_error_generic')});
        }
        return;
    }

    showValidationErrors($el, {_generic: t(status === 419 ? 'booking_session_expired' : 'auth_error_generic')});
    logClientEvent({message: 'auth request failed', status, step, context: describeError(err)});
}

/**
 * Same verdict as the server: App\Support\Phone::e164() -> Shared\Sms\PhoneCountry::toE164() +
 * isValidNumber (libphonenumber "max" metadata on both sides; "min" would only check the length and
 * let through numbers the server then refuses). Any spelling goes ("+40...", "0040...", "07...",
 * "7..."); a prefix-less number is read under the picked country, a "+.." one carries its own.
 * The two repairs of toE164() are mirrored, so the widget never blocks what the server accepts:
 *  - recoverDuplicatedPrefix: the picked country's code glued onto a qualified number ("+4040712..");
 *  - recoverBareCountryCode: the dial code without its plus ("40712..").
 *
 * @param {string} raw  what was typed
 * @param {string} iso  picked country, any case ('ro')
 * @returns {boolean}
 */
export function isValidPhone(raw, iso) {
    let clean = String(raw || '').trim();
    // PhoneCountry::sanitize: a "+" past the start means a prefix was typed twice; keep the last one.
    const lastPlus = clean.lastIndexOf('+');
    if (lastPlus > 0) clean = clean.slice(lastPlus);
    clean = clean.replace(/[^\d+]/g, '');
    if (clean.replace(/\D/g, '') === '') return false;

    const country = iso ? String(iso).toUpperCase() : undefined;
    const valid = (s) => {
        try { return isValidPhoneNumber(s, country); } catch (e) { return false; }
    };
    if (valid(clean)) return true;
    if (!country) return false;

    let cc;
    try { cc = getCountryCallingCode(country); } catch (e) { return false; }

    const prefix = '+' + cc;
    if (clean.startsWith(prefix)) {
        const tail = clean.slice(prefix.length);
        if (tail !== '' && (valid(tail) || valid('+' + tail))) return true;
    }
    return clean[0] !== '+' && clean[0] !== '0' && clean.startsWith(cc) && valid('+' + clean);
}

/**
 * Binds live phone number validation to phone input.
 *
 * @param {jQuery} $input - Input element for the phone number.
 * @param {Object} options - Validation options.
 * @param {boolean} [options.isInternational=true] - Whether to validate as international.
 * @param {boolean} [options.withCountryPicker=false] - A country <select> ($country) sits next to the
 *   field: the number is checked with isValidPhone() under the picked country. The row needs a
 *   `.field-phone-invalid-error` element.
 * @param {jQuery} [options.$country] - The picker <select> (withCountryPicker only).
 * @param {function(valid: boolean): void} [options.onValidChange] - Callback with validity state.
 * @returns {function(show: boolean): boolean|null} withCountryPicker: validate(show) - checks the
 *   current value, shows the message when `show` and the number is not valid; for the submit handler.
 */
export function bindPhoneValidation($input, {
    isInternational = true,
    withCountryPicker = false,
    $country = null,
    onValidChange = null
} = {}) {
    if (withCountryPicker) {
        const $row = $input.closest('.form-row');
        const validate = (show = false) => {
            const value = $input.val().trim();
            const ok = isValidPhone(value, $country ? $country.val() : null);
            $row.find('.field-phone-invalid-error').toggle(show && !ok && value !== '');
            if (typeof onValidChange === 'function') {
                onValidChange(ok);
            }
            return ok;
        };
        // While typing only the button follows the verdict: "not valid" under every keystroke of a
        // number still being typed would be shown to everyone. The text comes on blur and on submit.
        $input.on('input', () => validate(false));
        $input.on('blur', () => validate(true));
        // bindCountryPicker flips the select while "+36.." is being typed: no text in that case.
        if ($country) {
            $country.on('change', () => validate(!$input.is(':focus') && $input.val().trim() !== ''));
        }
        // A prefilled number (sharedState.phone) is never typed: judge it now.
        validate($input.val().trim() !== '');
        return validate;
    }

    $input.on('input', function () {
        const raw = $input.val().trim();
        const phoneNumber = raw.replace(/[^\d+]/g, '');
        const $row = $input.closest('.form-row');
        let isValid = true;

        // Hide all error messages first
        $row.find('.field-error').hide();

        if (!isInternational) {
            const startsWith07 = phoneNumber.startsWith('07');
            if (!startsWith07) {
                $row.find('.field-phone-ro-error').show();
                isValid = false;
            }
            if (phoneNumber.length !== 10) {
                $row.find('.field-phone-length-error').show();
                isValid = false;
            }
        } else {
            const startsWithZero = phoneNumber.startsWith('0');
            const startsWithPlus = phoneNumber.startsWith('+');
            if (!(startsWithZero || startsWithPlus)) {
                $row.find('.field-phone-ro-error').show();
                isValid = false;
            }
            if (phoneNumber.length <= 6) {
                $row.find('.field-phone-length-error').show();
                isValid = false;
            }
        }

        if (typeof onValidChange === 'function') {
            onValidChange(isValid);
        }
    });
    return null;
}
